import { clearAccountingReturnContext } from './accountingReturnContext';
import { clearLoginReturn } from './loginReturn';
import { createContext, useState, useContext, useEffect, useRef } from 'react';
import { apiFetch, storeCsrfToken, refreshSessionCoordinated, API_BASE } from '../api/apiFetch';
import { SESSION_IDLE_TIMEOUT, handleIdleTimeout } from '../api/idleTimeout';

/** Read verix_csrf cookie value (if set by server during login/refresh) */
function readCsrfCookie() {
  const match = document.cookie.match(/(?:^|;\s*)verix_csrf=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}
import { safeStorage } from '@/lib/safeStorage';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [isLoadingPublicSettings, setIsLoadingPublicSettings] = useState(true);
  const [authError, setAuthError] = useState(null);
  const hasCheckedRef = useRef(false);
  // True once an interactive login/OAuth exchange has run. Guards against a stale
  // bootstrap `/me` (fired before the session existed, e.g. on a cold-start Lambda)
  // resolving with 401 AFTER a successful login and tearing down the fresh cookies
  // — the "have to log in twice" bug.
  const loginInProgressRef = useRef(false);

  useEffect(() => {
    if (hasCheckedRef.current) return;
    hasCheckedRef.current = true;
    checkAppState();
  }, []);

  const checkAppState = async () => {
    try {
      setIsLoadingPublicSettings(false); // No public settings to load
      // Initial check: if cookies are invalid, call /logout to clear them so
      // we break out of any 401-redirect loop.
      await checkUserAuth({ clearStaleSessionOn401: true });
    } catch (error) {
       
      console.error('Unexpected error:', error);
      setAuthError({ type: 'unknown', message: error.message || 'An unexpected error occurred' });
      setIsLoadingPublicSettings(false);
      setIsLoadingAuth(false);
    }
  };

  const checkUserAuth = async ({ clearStaleSessionOn401 = false } = {}) => {
    setIsLoadingAuth(true);
    // true si un cierre por inactividad ya ha lanzado la navegación a
    // `login?reason=idle`: el estado se deja cargando para que el AuthGuard no
    // haga su propio <Navigate to="/login"> y se coma el motivo.
    let redirectingToLogin = false;
    try {
      let currentUser;
      try {
        currentUser = await apiFetch('/api/auth/me');
      } catch (meError) {
        // El access-token JWT caduca en 1h (Cognito) pero su cookie portadora
        // verix_token vive 4h y el refresh token 30 días: un 401 en /me casi
        // siempre es renovable, no una sesión muerta. apiFetch excluye
        // /api/auth/* de su auto-refresh, así que aquí canjeamos el refresh
        // token explícitamente antes de declarar la sesión inválida. Un único
        // intento — si falla, propaga y cae en la limpieza de abajo.
        if (meError?.status !== 401) throw meError;
        // Cierre por inactividad: el servidor ya borró la cookie de refresh, así
        // que canjearla solo da otro 401 y el usuario acababa en el login sin
        // saber por qué. Es la vía por la que caen la mayoría de estos cierres
        // (3 de 4 en los logs de dev del 25-09), porque `/me` corre en cada carga.
        if (meError?.code === SESSION_IDLE_TIMEOUT) {
          redirectingToLogin = handleIdleTimeout();
          throw meError;
        }
        // Mismo refresh coordinado que `apiFetch` (lock entre pestañas + REFRESH_RACE):
        // dos pestañas que arrancan a la vez con el access caducado no deben canjear
        // dos veces el mismo refresh token, porque con la rotación encendida la
        // segunda sería una reutilización y cerraría su sesión.
        let outcome;
        try {
          outcome = await refreshSessionCoordinated(async () => {
            // Solo corre si otra pestaña tenía el lock: si `/me` ya pasa, esa
            // pestaña refrescó y no hay que gastar el refresh token.
            try {
              currentUser = await apiFetch('/api/auth/me');
              return false;
            } catch {
              return true;
            }
          });
        } catch {
          throw meError;
        }
        // `refreshed` y `race` reintentan `/me` UNA vez: tras REFRESH_RACE la otra
        // petición ya dejó cookies válidas; si aun así da 401, la sesión está muerta
        // y cae en la limpieza de abajo.
        if (outcome !== 'already-refreshed') {
          currentUser = await apiFetch('/api/auth/me');
        }
      }
      setUser(currentUser.user);
      setIsAuthenticated(true);
      if (currentUser.csrfToken) {
        storeCsrfToken(currentUser.csrfToken);
      } else if (!sessionStorage.getItem('verix_csrf')) {
        const csrfValue = readCsrfCookie();
        if (csrfValue) storeCsrfToken(csrfValue);
      }
    } catch (error) {
      if (redirectingToLogin) return;
      // Race guard del "doble login": un 401/403 del check de bootstrap que resuelve
      // DESPUÉS de un login interactivo exitoso es stale — la request se emitió sin
      // cookie (p.ej. con la Lambda en cold start). Ignorarlo por completo: no revertir
      // isAuthenticated/user ni borrar la sesión recién creada. Sin esto, el bootstrap
      // tardío tumbaba el estado y el AuthGuard rebotaba al usuario a /login (= el bug
      // de tener que loguearse dos veces).
      if (
        clearStaleSessionOn401 &&
        loginInProgressRef.current &&
        (error?.status === 401 || error?.status === 403)
      ) {
        return;
      }
      setIsAuthenticated(false);
      setUser(null);
      if (error?.status === 401 || error?.status === 403) {
        // Solo el check inicial limpia cookies para romper un bucle de redirect 401,
        // y SOLO si había una sesión que limpiar.
        //
        // Un 401 en `/me` durante el arranque significa dos cosas muy distintas:
        //   · sesión muerta  → hay rastro de sesión previa en storage. Limpiar tiene
        //                      sentido: es lo que rompe el bucle de redirect.
        //   · visitante nuevo → no hay ningún rastro. Aquí no hay nada que limpiar, y
        //                      mandar un `logout` es a la vez inútil y peligroso.
        //
        // Peligroso porque abre una ventana de carrera: en una visita limpia la cadena
        // `/me` 401 → `refresh` 401 → este `catch` tarda ~0,7s, y si el usuario entra
        // en ese hueco, el `logout` viaja DESPUÉS del login. El guard de arriba no lo
        // atrapa porque cuando se evalúa el login todavía no había empezado
        // (`loginInProgressRef` era false). Resultado: cookie válida, `/me` en 200, y
        // aun así el AuthGuard rebota a /login porque el estado dice "deslogueado".
        //
        // Medido en el E2E Smoke contra dev (rojo desde el 06-08-2026): login 200,
        // /me 200 dos veces, y la app terminando en /login. Un humano tarda más de un
        // segundo en teclear credenciales y no lo ve; el test automático lo veía siempre.
        const habiaSesionPrevia =
          Boolean(sessionStorage.getItem('verix_csrf')) || Boolean(safeStorage.getItem('verix_user'));
        if (clearStaleSessionOn401 && habiaSesionPrevia) {
          try {
            await fetch(`${API_BASE}/api/auth/logout`, {
              method: 'POST',
              credentials: 'include',
            });
          } catch { /* noop */ }
          sessionStorage.removeItem('verix_csrf');
          safeStorage.removeItem('verix_user');
          safeStorage.removeItem('verix_company');
        }
        setAuthError({ type: 'auth_required', message: 'Authentication required' });
      } else {
        setAuthError({ type: 'unknown', message: error?.message || 'Error' });
      }
    } finally {
      if (!redirectingToLogin) setIsLoadingAuth(false);
    }
  };

  const refreshAuth = async () => {
    hasCheckedRef.current = true;
    // Set synchronously, before the await: refreshAuth runs right after the login
    // POST returns 200, so by the time a stale bootstrap `/me` resolves with 401
    // this flag is already up and the destructive cleanup is skipped.
    loginInProgressRef.current = true;
    await checkUserAuth({ clearStaleSessionOn401: false });
  };

  const logout = async (shouldRedirect = true) => {
    clearAccountingReturnContext();
    clearLoginReturn();
    setUser(null);
    setIsAuthenticated(false);
    // Clear server-side httpOnly cookies
    try {
      await apiFetch('/api/auth/logout', { method: 'POST' });
    } catch { /* ignore logout failures */ }
    // Only remove UI-only localStorage keys
    safeStorage.removeItem('verix_user');
    safeStorage.removeItem('verix_company');
    sessionStorage.removeItem('verix_csrf');
    if (shouldRedirect) {
      window.location.href = `${import.meta.env.BASE_URL}login`;
    }
  };

  const navigateToLogin = () => {
    window.location.href = `${import.meta.env.BASE_URL}login`;
  };

  // RGPD 7.1 — tras `POST /api/auth/me/accept-terms`, aplica la respuesta al usuario
  // en memoria en vez de llamar a `refreshAuth`: esa vuelve a poner `isLoadingAuth`
  // (el AuthGuard pintaría el loader) y levanta `loginInProgressRef`, que no es un
  // login. `terms_required` se fuerza a false solo si el servidor no lo manda.
  const markTermsAccepted = (fields) => {
    setUser((prev) => (prev ? { ...prev, terms_required: false, ...fields } : prev));
  };

  // XC-AI-013 — lo mismo para el aviso de IA en RRHH: aplica la respuesta de
  // `PUT /api/auth/me/ai-hr-optin` sin pasar por `refreshAuth`, que desde
  // Configuración desmontaría la página con el loader del AuthGuard.
  const applyHrAiOptIn = ({ ai_hr_optin, ai_hr_optin_at }) => {
    setUser((prev) => (prev ? { ...prev, ai_hr_optin, ai_hr_optin_at } : prev));
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      isLoadingAuth,
      isLoadingPublicSettings,
      authError,
      logout,
      navigateToLogin,
      checkAppState,
      refreshAuth,
      markTermsAccepted,
      applyHrAiOptIn
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
